import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Loader2, ArrowLeft, Send, Upload, Trash2, KeyRound } from "lucide-react";
import HardDeleteDialog from "@/components/HardDeleteDialog";

// ---------------------- Accounts list ----------------------
export function AccountsPage() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultAccountForm());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/accounts");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/accounts", {
        name: form.name,
        slug: form.slug,
        kapsystem: {
          username: form.username,
          password: form.password,
          sender_id: form.sender_id,
          dlt_entity_id: form.dlt_entity_id,
          base_url: form.base_url,
        },
      });
      toast.success("Account created");
      setOpen(false);
      setForm(defaultAccountForm());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Accounts"
        description="Tenants. Each account holds its own KAPSYSTEM credentials, templates and consent DB."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-account-button">
                <Plus className="mr-2 h-4 w-4" />
                New account
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="new-account-dialog">
              <DialogHeader>
                <DialogTitle>Create account</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <FormRow label="Name">
                  <Input
                    data-testid="account-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </FormRow>
                <FormRow label="Slug (a-z0-9-)">
                  <Input
                    data-testid="account-slug-input"
                    value={form.slug}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                      })
                    }
                  />
                </FormRow>
                <FormRow label="KAPSYSTEM username">
                  <Input
                    data-testid="account-kap-username"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                  />
                </FormRow>
                <FormRow label="KAPSYSTEM password">
                  <Input
                    data-testid="account-kap-password"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </FormRow>
                <FormRow label="Sender ID">
                  <Input
                    data-testid="account-kap-sender"
                    value={form.sender_id}
                    onChange={(e) => setForm({ ...form, sender_id: e.target.value })}
                  />
                </FormRow>
                <FormRow label="DLT entity ID">
                  <Input
                    data-testid="account-kap-entity"
                    value={form.dlt_entity_id}
                    onChange={(e) => setForm({ ...form, dlt_entity_id: e.target.value })}
                  />
                </FormRow>
              </div>
              <DialogFooter>
                <Button
                  onClick={submit}
                  disabled={busy || !form.name || !form.slug}
                  data-testid="account-create-submit"
                >
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card data-testid="accounts-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>KAPSYSTEM user</TableHead>
                <TableHead>Sender</TableHead>
                <TableHead>Active</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items === null && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <Loader2 className="inline h-4 w-4 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground text-sm">
                    No accounts yet.
                  </TableCell>
                </TableRow>
              )}
              {items?.map((a) => (
                <TableRow key={a.id} data-testid={`account-row-${a.slug}`}>
                  <TableCell className="font-medium">
                    <Link
                      to={`/accounts/${a.id}`}
                      className="hover:underline text-primary"
                      data-testid={`account-name-link-${a.slug}`}
                    >
                      {a.name}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{a.slug}</TableCell>
                  <TableCell className="text-xs">{a.kapsystem.username}</TableCell>
                  <TableCell className="font-mono text-xs">{a.kapsystem.sender_id}</TableCell>
                  <TableCell>
                    <Badge variant={a.is_active ? "default" : "secondary"}>
                      {a.is_active ? "active" : "inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="sm" asChild data-testid={`account-open-${a.slug}`}>
                      <Link to={`/accounts/${a.id}`}>Open</Link>
                    </Button>
                    <HardDeleteDialog
                      resourceType="account"
                      resourceName={a.name}
                      resourceId={a.id}
                      endpoint="/accounts"
                      onDeleted={load}
                      testId={`account-harddel-${a.slug}`}
                      trigger={
                        <Button variant="ghost" size="icon"
                          data-testid={`account-harddel-${a.slug}-btn`}
                          title="Delete permanently">
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function defaultAccountForm() {
  return {
    name: "",
    slug: "",
    username: "",
    password: "",
    sender_id: "ADCRFT",
    dlt_entity_id: "",
    base_url: "https://www.smsjust.com/blank/sms/user/urlsms.php",
  };
}

// ---------------------- Account detail ----------------------
export function AccountDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [acct, setAcct] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/accounts/${id}`);
      setAcct(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  if (!acct) {
    return (
      <div className="p-8">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav("/accounts")}
          data-testid="account-back"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to accounts
        </Button>
      </div>
      <PageHeader
        title={acct.name}
        description={
          <span className="font-mono text-xs">
            slug: {acct.slug} · kap user: {acct.kapsystem.username} · pass{" "}
            {acct.kapsystem.password_masked}
          </span>
        }
        actions={
          <Button asChild variant="outline" size="sm" data-testid="acct-goto-api-keys">
            <Link to={`/accounts/${id}/api-keys`}><KeyRound className="h-3 w-3 mr-1" />API Keys</Link>
          </Button>
        }
      />

      <Tabs defaultValue="general" data-testid="account-tabs">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="kapsystem" data-testid="tab-kapsystem">KAPSYSTEM</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="consent" data-testid="tab-consent">Consent</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="mt-4">
          <GeneralTab acct={acct} />
        </TabsContent>
        <TabsContent value="kapsystem" className="mt-4">
          <KapsystemTab acct={acct} reload={load} />
        </TabsContent>
        <TabsContent value="templates" className="mt-4">
          <TemplatesTab acctId={id} />
        </TabsContent>
        <TabsContent value="consent" className="mt-4">
          <ConsentTab acctId={id} />
        </TabsContent>
        <TabsContent value="projects" className="mt-4">
          <ProjectsTab acctId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GeneralTab({ acct }) {
  return (
    <Card data-testid="general-card">
      <CardContent className="pt-6 space-y-2 text-sm">
        <KV k="ID" v={<span className="font-mono text-xs">{acct.id}</span>} />
        <KV k="Name" v={acct.name} />
        <KV k="Slug" v={<span className="font-mono">{acct.slug}</span>} />
        <KV k="Active" v={acct.is_active ? "yes" : "no"} />
        <KV k="Batch size" v={acct.defaults?.batch_size} />
        <KV k="Delay (s)" v={acct.defaults?.delay_seconds} />
        <KV k="Cost / SMS (paise)" v={acct.defaults?.cost_per_sms_paise} />
        <KV k="Max recipients / campaign" v={acct.defaults?.max_recipients} />
        <KV k="Max cost / campaign (public API, ₹)" v={((acct.defaults?.max_campaign_cost_paise ?? 500000) / 100).toFixed(0)} />
        <KV k="Max daily cost (public API, ₹/24h)" v={((acct.defaults?.max_daily_cost_paise ?? 2500000) / 100).toFixed(0)} />
        <KV k="Timezone" v={acct.defaults?.tz} />
      </CardContent>
    </Card>
  );
}

function KapsystemTab({ acct, reload }) {
  const [mobile, setMobile] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const test = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/accounts/${acct.id}/test-kapsystem`, {
        mobile,
        dry_run: dryRun,
      });
      setResult(data);
      toast.success(dryRun ? "Dry-run built" : "Test send completed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card data-testid="kapsystem-tab-card">
        <CardHeader>
          <CardTitle>KAPSYSTEM credentials</CardTitle>
          <CardDescription>
            Password stored encrypted (Fernet) at rest. Only last 4 chars shown.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <KV k="Username" v={acct.kapsystem.username} />
          <KV k="Password" v={<span className="font-mono">{acct.kapsystem.password_masked}</span>} />
          <KV k="Sender ID" v={<span className="font-mono">{acct.kapsystem.sender_id}</span>} />
          <KV k="DLT entity ID" v={<span className="font-mono">{acct.kapsystem.dlt_entity_id}</span>} />
          <KV k="Base URL" v={<span className="font-mono text-xs">{acct.kapsystem.base_url}</span>} />
          <div className="mt-6 pt-4 border-t space-y-3">
            <div className="text-sm font-medium">Test send to one number</div>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label htmlFor="k-mobile" className="text-xs">
                  Mobile
                </Label>
                <Input
                  id="k-mobile"
                  data-testid="kaptest-mobile-input"
                  placeholder="9876543210"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={dryRun}
                  onCheckedChange={setDryRun}
                  data-testid="kaptest-dryrun-switch"
                />
                <Label className="text-xs">dry_run</Label>
              </div>
              <Button
                onClick={test}
                disabled={busy || !mobile}
                variant={dryRun ? "default" : "destructive"}
                data-testid="kaptest-send-button"
              >
                {busy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                {dryRun ? "Build" : "SEND REAL"}
              </Button>
            </div>
            {result && (
              <pre
                data-testid="kaptest-result"
                className="text-xs whitespace-pre-wrap break-all bg-muted p-3 rounded"
              >
                {JSON.stringify(result, null, 2)}
              </pre>
            )}
          </div>
        </CardContent>
      </Card>

      <DlrConfigCard acct={acct} reload={reload} />
    </div>
  );
}

function DlrConfigCard({ acct, reload }) {
  const kap = acct.kapsystem || {};
  const [form, setForm] = useState({
    dlr_endpoint_url: kap.dlr_endpoint_url || "",
    dlr_response_format: kap.dlr_response_format || "auto",
    dlr_poll_interval_seconds: kap.dlr_poll_interval_seconds || 60,
    dlr_max_poll_duration_hours: kap.dlr_max_poll_duration_hours || 24,
    param_username: (kap.dlr_param_map || {}).username_param || "username",
    param_password: (kap.dlr_param_map || {}).password_param || "pass",
    param_msgid: (kap.dlr_param_map || {}).msgid_param || "Scheduleid",
    dlr_include_username: !!kap.dlr_include_username,
    dlr_include_password: !!kap.dlr_include_password,
    dlr_scheduleid_mode: kap.dlr_scheduleid_mode || "auto",
  });
  const [busy, setBusy] = useState(false);
  const [sampleId, setSampleId] = useState("");
  const [dryRun, setDryRun] = useState(true);   // safe default pre-whitelist
  const [testResult, setTestResult] = useState(null);
  const [healthResult, setHealthResult] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/accounts/${acct.id}/dlr-config`, {
        dlr_endpoint_url: form.dlr_endpoint_url || null,
        dlr_param_map: {
          username_param: form.param_username,
          password_param: form.param_password,
          msgid_param: form.param_msgid,
        },
        dlr_response_format: form.dlr_response_format,
        dlr_poll_interval_seconds: Number(form.dlr_poll_interval_seconds),
        dlr_max_poll_duration_hours: Number(form.dlr_max_poll_duration_hours),
        dlr_include_username: form.dlr_include_username,
        dlr_include_password: form.dlr_include_password,
        dlr_scheduleid_mode: form.dlr_scheduleid_mode,
      });
      toast.success("DLR config saved");
      reload && reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    if (!sampleId) return;
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/accounts/${acct.id}/dlr/test`, {
        sample_message_id: sampleId,
        dry_run: dryRun,
      });
      setTestResult(data);
      if (data.dry_run) {
        toast.success("Constructed URL preview (no network call)");
      } else if (data.parser_confidence === "low") {
        toast.warning("Parser confidence LOW — response format may have drifted.");
      } else {
        toast.success(`Parsed ${data.parsed_rows?.length || 0} row(s)`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runHealth = async () => {
    setHealthBusy(true);
    setHealthResult(null);
    try {
      const { data } = await api.get(`/accounts/${acct.id}/dlr/health`);
      setHealthResult(data);
      if (data.ok && data.live) toast.success("DLR endpoint healthy (LIVE)");
      else if (data.ok) toast.info("DLR endpoint healthy (mock mode)");
      else toast.warning(data.reason || "DLR health check reported issues");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHealthBusy(false);
    }
  };

  return (
    <Card data-testid="dlr-config-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          Delivery Report (DLR) polling
          {kap.dlr_enabled ? (
            <Badge variant="default" className="text-[10px]">enabled</Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">disabled</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Configure the KAPSYSTEM DLR endpoint so SMSCraft can poll delivered/failed
          status for each message after send. Leave endpoint blank to disable.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <FormRow label="DLR endpoint URL">
          <Input
            data-testid="dlr-endpoint-url"
            placeholder="https://www.smsjust.com/blank/sms/user/dlrsms.php"
            value={form.dlr_endpoint_url}
            onChange={(e) => setForm({ ...form, dlr_endpoint_url: e.target.value })}
          />
        </FormRow>
        <div className="grid grid-cols-3 gap-3">
          <FormRow label="username param">
            <Input
              data-testid="dlr-param-username"
              value={form.param_username}
              onChange={(e) => setForm({ ...form, param_username: e.target.value })}
            />
          </FormRow>
          <FormRow label="password param">
            <Input
              data-testid="dlr-param-password"
              value={form.param_password}
              onChange={(e) => setForm({ ...form, param_password: e.target.value })}
            />
          </FormRow>
          <FormRow label="msgid param">
            <Input
              data-testid="dlr-param-msgid"
              value={form.param_msgid}
              onChange={(e) => setForm({ ...form, param_msgid: e.target.value })}
            />
          </FormRow>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <FormRow label="Response format">
            <Select
              value={form.dlr_response_format}
              onValueChange={(v) => setForm({ ...form, dlr_response_format: v })}
            >
              <SelectTrigger data-testid="dlr-format-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">auto</SelectItem>
                <SelectItem value="json">json</SelectItem>
                <SelectItem value="csv">csv/pipe-delim</SelectItem>
              </SelectContent>
            </Select>
          </FormRow>
          <FormRow label="Scheduleid mode">
            <Select
              value={form.dlr_scheduleid_mode}
              onValueChange={(v) => setForm({ ...form, dlr_scheduleid_mode: v })}
            >
              <SelectTrigger data-testid="dlr-scheduleid-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">auto (try full → prefix)</SelectItem>
                <SelectItem value="full">full (e.g. 5751791693-2026_07_07)</SelectItem>
                <SelectItem value="prefix">prefix (e.g. 5751791693)</SelectItem>
              </SelectContent>
            </Select>
          </FormRow>
          <FormRow label="Poll interval (s)">
            <Input
              type="number"
              min="10"
              data-testid="dlr-poll-interval"
              value={form.dlr_poll_interval_seconds}
              onChange={(e) => setForm({ ...form, dlr_poll_interval_seconds: e.target.value })}
            />
          </FormRow>
        </div>
        <div className="grid grid-cols-3 gap-3 items-end">
          <FormRow label="Max poll (hours)">
            <Input
              type="number"
              min="1"
              max="72"
              data-testid="dlr-poll-max-hours"
              value={form.dlr_max_poll_duration_hours}
              onChange={(e) => setForm({ ...form, dlr_max_poll_duration_hours: e.target.value })}
            />
          </FormRow>
          <label className="flex items-center gap-2 text-xs cursor-pointer h-9">
            <input type="checkbox" data-testid="dlr-include-username"
              checked={form.dlr_include_username}
              onChange={(e) => setForm({ ...form, dlr_include_username: e.target.checked })} />
            Send <code className="font-mono">username</code> in URL
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer h-9">
            <input type="checkbox" data-testid="dlr-include-password"
              checked={form.dlr_include_password}
              onChange={(e) => setForm({ ...form, dlr_include_password: e.target.checked })} />
            Send <code className="font-mono">password</code> in URL
          </label>
        </div>
        <div className="text-[10px] text-muted-foreground -mt-2">
          KAPSYSTEM DLR uses IP-whitelist auth — leave both boxes off. Enable only if switching to a vendor that requires them.
        </div>
        <div>
          <Button onClick={save} disabled={busy} data-testid="dlr-save-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save DLR config
          </Button>
        </div>

        <div className="mt-6 pt-4 border-t space-y-3">
          <div className="text-sm font-medium">Test DLR with a sample message ID</div>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Sample KAPSYSTEM message_id</Label>
              <Input
                data-testid="dlr-sample-msgid"
                placeholder="1234567890-2026_02_01"
                value={sampleId}
                onChange={(e) => setSampleId(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              onClick={runTest}
              disabled={busy || !sampleId || !form.dlr_endpoint_url}
              data-testid="dlr-test-btn"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dryRun ? "Preview URL" : "Test live"}
            </Button>
            <label className="flex items-center gap-2 text-xs cursor-pointer h-9">
              <input type="checkbox" data-testid="dlr-test-dryrun"
                checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              Dry-run (no network)
            </label>
            <Button
              variant="secondary"
              onClick={runHealth}
              disabled={healthBusy || !form.dlr_endpoint_url}
              data-testid="dlr-health-btn"
              title="Hit the DLR endpoint ONCE with a real recent message id from this account"
            >
              {healthBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Run health check
            </Button>
          </div>
          {healthResult && (
            <div className="rounded border bg-muted/30 p-3 space-y-2" data-testid="dlr-health-result">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={healthResult.ok ? "default" : "destructive"} className="text-[10px]">
                  {healthResult.ok ? "healthy" : "unhealthy"}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {healthResult.live ? "LIVE" : "mock"}
                </Badge>
                {healthResult.winner?.mode && (
                  <Badge variant="secondary" className="text-[10px]" data-testid="dlr-health-winner-mode">
                    winning mode: {healthResult.winner.mode}
                  </Badge>
                )}
                {healthResult.winner?.mode_cached && (
                  <Badge variant="default" className="text-[10px]" title="Winning mode cached on this account">
                    ✓ cached
                  </Badge>
                )}
                {healthResult.sample_message_id && (
                  <span className="text-[10px] font-mono text-muted-foreground">
                    probed: {healthResult.sample_message_id}
                  </span>
                )}
              </div>
              {healthResult.reason && (
                <div className="text-xs text-muted-foreground italic">{healthResult.reason}
                  {healthResult.hint && <> — {healthResult.hint}</>}
                </div>
              )}
              {healthResult.attempts?.map((a, i) => (
                <div key={i} className="border-l-2 pl-2 border-muted-foreground/30">
                  <div className="text-xs">
                    <b>attempt {i + 1}</b> · mode={a.mode} · HTTP={a.http_status ?? "—"} · parser={a.parser_confidence}
                    {a.error && <span className="text-destructive"> · {a.error}</span>}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground break-all">{a.constructed_url}</div>
                  {a.raw_response && (
                    <pre className="text-[10px] bg-background rounded p-2 mt-1 max-h-24 overflow-auto whitespace-pre-wrap">{a.raw_response}</pre>
                  )}
                </div>
              ))}
            </div>
          )}
          {testResult && (
            <div className="space-y-2">
              {testResult.parser_confidence && (
                <Badge
                  variant={testResult.parser_confidence === "high" ? "default" : "destructive"}
                  className="text-[10px]"
                  data-testid="dlr-parser-conf"
                >
                  parser: {testResult.parser_confidence}
                </Badge>
              )}
              <div className="text-xs text-muted-foreground font-mono break-all">
                {testResult.masked_url}
              </div>
              {testResult.parsed_rows?.length > 0 && (
                <div className="rounded border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>msgid</TableHead>
                        <TableHead>status</TableHead>
                        <TableHead>detail</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {testResult.parsed_rows.map((r, i) => (
                        <TableRow key={i} data-testid={`dlr-parsed-row-${i}`}>
                          <TableCell className="font-mono text-xs">{r.msgid}</TableCell>
                          <TableCell>
                            <Badge variant={r.status === "delivered" ? "default" : r.status === "failed" ? "destructive" : "secondary"} className="text-[10px]">{r.status}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">{r.detail}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <details>
                <summary className="text-xs text-muted-foreground cursor-pointer">Raw response</summary>
                <pre className="text-[10px] whitespace-pre-wrap break-all bg-muted p-2 rounded mt-1 max-h-40 overflow-auto">
                  {testResult.raw_response}
                </pre>
              </details>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TemplatesTab({ acctId }) {
  const [items, setItems] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/accounts/${acctId}/templates`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [acctId]);

  const importCsv = async (commit) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/accounts/${acctId}/templates/bulk-import?commit=${commit ? "true" : "false"}`,
        fd,
      );
      toast.success(
        commit
          ? `Imported ${data.inserted} template(s) (${data.total_rows} rows)`
          : `Preview: would insert ${data.would_insert} of ${data.total_rows}`,
      );
      if (commit) load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get(`/accounts/${acctId}/templates/export`);
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "templates.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <Card data-testid="templates-import-card">
        <CardHeader>
          <CardTitle className="text-base">Bulk import DLT templates (CSV)</CardTitle>
          <CardDescription>
            Same format as the client&apos;s ContentTemplates.csv. Idempotent by DLT id.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="templates-import-file"
            className="max-w-sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => importCsv(false)}
            disabled={!file || busy}
            data-testid="templates-import-preview"
          >
            Preview
          </Button>
          <Button
            size="sm"
            onClick={() => importCsv(true)}
            disabled={!file || busy}
            data-testid="templates-import-commit"
          >
            <Upload className="mr-2 h-4 w-4" />
            Import
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="templates-list-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Templates</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            data-testid="templates-export-csv"
          >
            Download CSV
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>DLT ID</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Header</TableHead>
                <TableHead>Vars</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items === null && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6">
                    <Loader2 className="inline h-4 w-4 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-xs text-muted-foreground">
                    No templates.
                  </TableCell>
                </TableRow>
              )}
              {items?.map((t) => (
                <TableRow key={t.id} data-testid={`template-row-${t.dlt_template_id}`}>
                  <TableCell className="text-xs">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs">{t.dlt_template_id}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {t.category.replace("_", " ")}
                    </Badge>
                    {t.requires_consent && (
                      <Badge variant="destructive" className="ml-1 text-[10px]">
                        consent
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{t.header_sender_ids.join(", ")}</TableCell>
                  <TableCell className="text-xs">{t.variables.length}</TableCell>
                  <TableCell>
                    <Badge variant={t.status === "active" ? "default" : "secondary"}>
                      {t.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ConsentTab({ acctId }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState({ phone: "", scope: "", source: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    phone: "",
    consent_scope: "engineering admission counselling",
    consent_source: "web_form",
    consent_proof_url: "",
  });
  const [checkPhones, setCheckPhones] = useState("");
  const [checkScope, setCheckScope] = useState("engineering");
  const [checkResult, setCheckResult] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (q.phone) params.set("phone", q.phone);
      if (q.scope) params.set("scope", q.scope);
      if (q.source) params.set("source", q.source);
      const { data } = await api.get(
        `/accounts/${acctId}/consent?${params.toString()}`,
      );
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [acctId]);

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/accounts/${acctId}/consent`, {
        phone: form.phone,
        consent_scope: form.consent_scope,
        consent_source: form.consent_source,
        consent_proof_url: form.consent_proof_url || undefined,
      });
      toast.success("Consent added");
      setAddOpen(false);
      setForm({ ...form, phone: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runCheck = async () => {
    const phones = checkPhones
      .split(/[,\n\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!phones.length) return;
    try {
      const { data } = await api.post(
        `/accounts/${acctId}/consent/check`,
        { phones, scope: checkScope },
      );
      setCheckResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const importCsv = async (commit) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/accounts/${acctId}/consent/bulk-import?commit=${commit}`,
        fd,
      );
      toast.success(
        commit
          ? `Imported ${data.inserted} record(s)`
          : `Preview: would insert ${data.would_insert} of ${data.total_rows}`,
      );
      if (commit) load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (row) => {
    try {
      await api.delete(`/consent/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <Card data-testid="consent-add-card">
        <CardHeader>
          <CardTitle className="text-base">Add / Import consent</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="consent-add-open">
                <Plus className="mr-2 h-4 w-4" />
                Add one
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="consent-add-dialog">
              <DialogHeader>
                <DialogTitle>Add consent record</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <FormRow label="Phone">
                  <Input
                    data-testid="consent-phone-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="9876543210"
                  />
                </FormRow>
                <FormRow label="Scope">
                  <Input
                    data-testid="consent-scope-input"
                    value={form.consent_scope}
                    onChange={(e) => setForm({ ...form, consent_scope: e.target.value })}
                  />
                </FormRow>
                <FormRow label="Source">
                  <Select
                    value={form.consent_source}
                    onValueChange={(v) => setForm({ ...form, consent_source: v })}
                  >
                    <SelectTrigger data-testid="consent-source-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="web_form">web_form</SelectItem>
                      <SelectItem value="csv_import">csv_import</SelectItem>
                      <SelectItem value="manual">manual</SelectItem>
                    </SelectContent>
                  </Select>
                </FormRow>
                <FormRow label="Proof URL (optional)">
                  <Input
                    data-testid="consent-proof-input"
                    value={form.consent_proof_url}
                    onChange={(e) => setForm({ ...form, consent_proof_url: e.target.value })}
                  />
                </FormRow>
              </div>
              <DialogFooter>
                <Button
                  onClick={add}
                  disabled={busy || !form.phone || !form.consent_scope}
                  data-testid="consent-add-submit"
                >
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Input
            type="file"
            accept=".csv"
            className="max-w-sm"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="consent-import-file"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => importCsv(false)}
            disabled={!file || busy}
            data-testid="consent-import-preview"
          >
            Preview
          </Button>
          <Button
            size="sm"
            onClick={() => importCsv(true)}
            disabled={!file || busy}
            data-testid="consent-import-commit"
          >
            <Upload className="mr-2 h-4 w-4" />
            Import
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="consent-export">
            <a
              href={`${api.defaults.baseURL}/accounts/${acctId}/consent/export`}
              onClick={(e) => {
                // header-auth download
                e.preventDefault();
                api
                  .get(`/accounts/${acctId}/consent/export`)
                  .then((r) => {
                    const blob = new Blob([r.data], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "consent.csv";
                    a.click();
                    URL.revokeObjectURL(url);
                  })
                  .catch((err) => toast.error(formatApiError(err)));
              }}
            >
              Export CSV
            </a>
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="consent-check-card">
        <CardHeader>
          <CardTitle className="text-base">Bulk consent check</CardTitle>
          <CardDescription>
            Given a list of phones and a scope, returns consented vs not-consented.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            data-testid="consent-check-phones"
            rows={3}
            placeholder="9876543210, 9198xxxxxxx"
            value={checkPhones}
            onChange={(e) => setCheckPhones(e.target.value)}
          />
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label className="text-xs">Scope (substring match)</Label>
              <Input
                data-testid="consent-check-scope"
                value={checkScope}
                onChange={(e) => setCheckScope(e.target.value)}
              />
            </div>
            <Button onClick={runCheck} data-testid="consent-check-run">
              Check
            </Button>
          </div>
          {checkResult && (
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="default">consented: {checkResult.consented.length}</Badge>
              <Badge variant="destructive">
                not consented: {checkResult.not_consented.length}
              </Badge>
              <Badge variant="secondary">invalid: {checkResult.invalid.length}</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="consent-list-card">
        <CardHeader>
          <CardTitle className="text-base">Records</CardTitle>
          <div className="flex flex-wrap gap-2 pt-2">
            <Input
              placeholder="Phone filter"
              className="max-w-[180px] text-xs"
              value={q.phone}
              onChange={(e) => setQ({ ...q, phone: e.target.value })}
              data-testid="consent-filter-phone"
            />
            <Input
              placeholder="Scope substring"
              className="max-w-[220px] text-xs"
              value={q.scope}
              onChange={(e) => setQ({ ...q, scope: e.target.value })}
              data-testid="consent-filter-scope"
            />
            <Button size="sm" variant="outline" onClick={load} data-testid="consent-filter-run">
              Apply
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Phone</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Date</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items === null && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6">
                    <Loader2 className="inline h-4 w-4 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-xs text-muted-foreground">
                    No records.
                  </TableCell>
                </TableRow>
              )}
              {items?.map((r) => (
                <TableRow key={r.id} data-testid={`consent-row-${r.phone_e164}`}>
                  <TableCell className="font-mono text-xs">{r.phone_e164}</TableCell>
                  <TableCell className="text-xs">{r.consent_scope}</TableCell>
                  <TableCell className="text-xs">{r.consent_source}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(r.consent_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => del(r)}
                      data-testid={`consent-del-${r.phone_e164}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ProjectsTab({ acctId }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects?account_id=${acctId}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [acctId]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/projects", { account_id: acctId, name, description: desc });
      toast.success("Project created");
      setOpen(false);
      setName("");
      setDesc("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="projects-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Projects</CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="project-new-open">
              <Plus className="mr-2 h-4 w-4" />
              New project
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="project-new-dialog">
            <DialogHeader>
              <DialogTitle>Create project</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <FormRow label="Name">
                <Input
                  data-testid="project-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </FormRow>
              <FormRow label="Description">
                <Textarea
                  data-testid="project-desc-input"
                  rows={2}
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                />
              </FormRow>
            </div>
            <DialogFooter>
              <Button
                onClick={submit}
                disabled={busy || !name}
                data-testid="project-create-submit"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items === null && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-6">
                  <Loader2 className="inline h-4 w-4 animate-spin" />
                </TableCell>
              </TableRow>
            )}
            {items?.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-6 text-xs text-muted-foreground">
                  No projects.
                </TableCell>
              </TableRow>
            )}
            {items?.map((p) => (
              <TableRow key={p.id} data-testid={`project-row-${p.name.replace(/\s+/g, "_")}`}>
                <TableCell className="font-medium">
                  <Link to={`/projects/${p.id}`}
                        className="hover:underline text-primary"
                        data-testid={`project-name-link-tab-${p.id}`}>
                    {p.name}
                  </Link>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {p.description || "—"}
                </TableCell>
                <TableCell>
                  <Badge variant={p.is_active ? "default" : "secondary"}>
                    {p.is_active ? "active" : "inactive"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------------------- Shared bits ----------------------
export function PageHeader({ title, description, action, actions }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="page-title">
          {title}
        </h1>
        {description && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      {actions || action}
    </div>
  );
}

export function FormRow({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs flex items-baseline gap-2">
        <span>{label}</span>
        {hint && <span className="text-[10px] text-muted-foreground font-normal">{hint}</span>}
      </Label>
      {children}
    </div>
  );
}

export function KV({ k, v }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-3 py-1 text-sm">
      <div className="text-muted-foreground">{k}</div>
      <div>{v ?? "—"}</div>
    </div>
  );
}
